import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useBusinesses } from '@/context/BusinessContext';
import {
  createBusiness,
  ENTITY_TYPES,
  ENTITY_TYPE_LABELS,
  type CreateBusinessInput,
  type EntityType,
} from '@/lib/api';
import { SALE_TAX_OPTIONS, SALE_TAX_GROUPS } from '@/lib/saleTaxOptions';

type Profile = 'products' | 'services' | 'both';

type CategoryType = 'products' | 'services';

interface CategoryRow {
  id: string;
  name: string;
  type: CategoryType;
  /** tax_codes.code to bind, or null for no tax. */
  tax: string | null;
}

interface FormState {
  name: string;
  entity_type: EntityType | null;
  business_address: string;
  business_email: string;
  business_phone: string;
  profile: Profile | null;
  tax_registered_sales_tax: boolean;
  tax_registered_goods_tax: boolean;
  tax_registered_withholding: boolean;
  tax_registered_paye_employer: boolean;
  tax_exempt: boolean;
  tax_exempt_reason: string;
  categories: CategoryRow[];
}

const INITIAL_FORM: FormState = {
  name: '',
  entity_type: null,
  business_address: '',
  business_email: '',
  business_phone: '',
  profile: null,
  tax_registered_sales_tax: false,
  tax_registered_goods_tax: false,
  tax_registered_withholding: false,
  tax_registered_paye_employer: false,
  tax_exempt: false,
  tax_exempt_reason: '',
  categories: [],
};

const PROFILE_OPTIONS: { value: Profile; label: string; help: string }[] = [
  { value: 'products', label: 'Products', help: 'You mainly sell goods or stock.' },
  { value: 'services', label: 'Services', help: 'You mainly sell services or labour.' },
  { value: 'both', label: 'Both', help: 'You sell goods and services.' },
];

const STEP_TITLES = [
  'About your business',
  'Business contact',
  'Tax registrations',
  'What you sell',
  'Review & finish',
];

/** Default categories mirroring the server seed rule, used to pre-fill the step. */
function defaultCategories(f: FormState): CategoryRow[] {
  const rows: CategoryRow[] = [];
  if (f.profile === 'services' || f.profile === 'both') {
    rows.push({
      id: crypto.randomUUID(),
      name: 'Services',
      type: 'services',
      tax: f.tax_registered_sales_tax ? 'ST' : null,
    });
  }
  if (f.profile === 'products' || f.profile === 'both') {
    rows.push({ id: crypto.randomUUID(), name: 'Products', type: 'products', tax: null });
  }
  return rows;
}

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

const inputClass =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100';

function CheckboxField(props: {
  label: string;
  help: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 p-3 transition hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-800"
      />
      <span>
        <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
          {props.label}
        </span>
        <span className="block text-xs text-slate-500 dark:text-slate-400">{props.help}</span>
      </span>
    </label>
  );
}

export function Onboarding({ mode = 'first' }: { mode?: 'first' | 'add' }) {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { businesses, loading, addBusiness, setActiveBusinessId } = useBusinesses();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
      </div>
    );
  }
  if (mode === 'first' && businesses.length > 0) {
    return <Navigate to="/dashboard" replace />;
  }
  if (mode === 'add' && businesses.length >= 4) {
    return <Navigate to="/dashboard" replace />;
  }

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const emailValid = isValidEmail(form.business_email.trim());
  const step0Valid =
    form.name.trim().length > 0 && form.entity_type !== null && form.profile !== null;
  const step1Valid =
    form.business_address.trim().length > 0 && emailValid && form.business_phone.trim().length > 0;

  const availableTypes: CategoryType[] =
    form.profile === 'both'
      ? ['products', 'services']
      : form.profile === 'products'
        ? ['products']
        : form.profile === 'services'
          ? ['services']
          : [];
  const applicableTaxOptions = SALE_TAX_OPTIONS.filter(
    (o) => o.flag === null || form[o.flag],
  );
  const categoriesValid =
    form.categories.length > 0 && form.categories.every((c) => c.name.trim().length > 0);

  const canAdvance =
    (step === 0 && step0Valid) ||
    (step === 1 && step1Valid) ||
    step === 2 ||
    (step === 3 && categoriesValid);

  function goNext() {
    // Entering the categories step: pre-fill from defaults if empty, and
    // reconcile any prior edits against the current profile / sales-tax
    // registration (the user may have gone back and changed what they sell).
    if (step === 2) {
      setForm((prev) => {
        const types: CategoryType[] =
          prev.profile === 'both'
            ? ['products', 'services']
            : prev.profile === 'products'
              ? ['products']
              : prev.profile === 'services'
                ? ['services']
                : [];
        const applicable = new Set(
          SALE_TAX_OPTIONS.filter((o) => o.flag === null || prev[o.flag]).map((o) => o.code),
        );
        let cats = prev.categories.map((c) => ({
          ...c,
          type: types.includes(c.type) ? c.type : (types[0] ?? 'services'),
          tax: c.tax && !applicable.has(c.tax) ? null : c.tax,
        }));
        if (cats.length === 0) cats = defaultCategories(prev);
        return { ...prev, categories: cats };
      });
    }
    setStep((s) => s + 1);
  }

  function updateCategory(index: number, patch: Partial<CategoryRow>) {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    }));
  }
  function addCategory() {
    setForm((prev) => ({
      ...prev,
      categories: [
        ...prev.categories,
        {
          id: crypto.randomUUID(),
          name: '',
          type: (availableTypes[0] ?? 'services') as CategoryType,
          tax: null,
        },
      ],
    }));
  }
  function removeCategory(index: number) {
    setForm((prev) => ({
      ...prev,
      categories: prev.categories.filter((_, i) => i !== index),
    }));
  }

  async function handleCreate() {
    if (form.profile === null || form.entity_type === null) return;
    setSubmitting(true);
    setError(null);
    try {
      const input: CreateBusinessInput = {
        name: form.name.trim(),
        entity_type: form.entity_type,
        business_address: form.business_address.trim(),
        business_email: form.business_email.trim(),
        business_phone: form.business_phone.trim(),
        sells_products: form.profile === 'products' || form.profile === 'both',
        sells_services: form.profile === 'services' || form.profile === 'both',
        tax_registered_sales_tax: form.tax_registered_sales_tax,
        tax_registered_goods_tax: form.tax_registered_goods_tax,
        tax_registered_withholding: form.tax_registered_withholding,
        tax_registered_paye_employer: form.tax_registered_paye_employer,
        tax_exempt: form.tax_exempt,
        tax_exempt_reason: form.tax_exempt ? form.tax_exempt_reason.trim() || null : null,
        sale_categories: form.categories.map((c) => ({
          name: c.name.trim(),
          revenue_account_code: c.type === 'products' ? '4010' : '4020',
          tax_code: c.tax,
        })),
      };
      const token = await getToken();
      const business = await createBusiness(token, input);
      addBusiness(business);
      setActiveBusinessId(business.id);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not create your business. Please try again.',
      );
      setSubmitting(false);
    }
  }

  const profileLabel =
    form.profile === 'both'
      ? 'Products and services'
      : form.profile === 'products'
        ? 'Products'
        : form.profile === 'services'
          ? 'Services'
          : '—';

  const registrations = [
    form.tax_registered_sales_tax ? 'Sales Tax' : null,
    form.tax_registered_goods_tax ? 'Goods Tax' : null,
    form.tax_registered_withholding ? 'Withholding Tax' : null,
    form.tax_registered_paye_employer ? 'PAYE (employer)' : null,
  ].filter((r): r is string => r !== null);

  return (
    <div className="mx-auto max-w-xl px-4 py-10 sm:px-6">
      <header className="mb-8 flex items-center justify-between">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">WanEntry</h1>
        <div className="flex items-center gap-2">
          {mode === 'add' && (
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex h-9 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          )}
          <ThemeToggle />
        </div>
      </header>

      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-brand-600 dark:text-brand-400">
          Step {step + 1} of {STEP_TITLES.length}
        </p>
        <h2 className="mb-6 text-2xl font-bold text-slate-900 dark:text-slate-100">
          {STEP_TITLES[step]}
        </h2>

        {step === 0 && (
          <div className="space-y-6">
            <div>
              <label
                htmlFor="business-name"
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Business name
              </label>
              <input
                id="business-name"
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                placeholder="e.g. Honiara Hardware"
                className={inputClass}
              />
            </div>

            <div>
              <label
                htmlFor="entity-type"
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Business structure
              </label>
              <select
                id="entity-type"
                value={form.entity_type ?? ''}
                onChange={(e) =>
                  update('entity_type', e.target.value === '' ? null : (e.target.value as EntityType))
                }
                className={inputClass}
              >
                <option value="" disabled>
                  Select a structure…
                </option>
                {ENTITY_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {ENTITY_TYPE_LABELS[value]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Your legal structure. This affects how income tax and equity are handled in your
                accounts.
              </p>
            </div>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                What does this business mainly do?
              </legend>
              <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                This sets up the right accounts — for example, a services-only business won&apos;t
                get inventory or cost-of-goods accounts. This can&apos;t be changed later yet, so
                pick what fits best.
              </p>
              <div className="grid gap-2 sm:grid-cols-3">
                {PROFILE_OPTIONS.map((opt) => {
                  const selected = form.profile === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => update('profile', opt.value)}
                      className={`rounded-md border p-3 text-left transition ${
                        selected
                          ? 'border-brand-500 bg-brand-50 dark:border-brand-400 dark:bg-brand-950/40'
                          : 'border-slate-200 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800/50'
                      }`}
                    >
                      <span className="block text-sm font-medium text-slate-900 dark:text-slate-100">
                        {opt.label}
                      </span>
                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                        {opt.help}
                      </span>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              These appear on the letterhead of reports you generate, so use the details you want
              customers and IRD to see.
            </p>
            <div>
              <label
                htmlFor="business-address"
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Business address
              </label>
              <textarea
                id="business-address"
                value={form.business_address}
                onChange={(e) => update('business_address', e.target.value)}
                placeholder="e.g. PO Box 123, Honiara, Guadalcanal"
                rows={2}
                className={inputClass}
              />
            </div>
            <div>
              <label
                htmlFor="business-email"
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Business email
              </label>
              <input
                id="business-email"
                type="email"
                value={form.business_email}
                onChange={(e) => update('business_email', e.target.value)}
                placeholder="e.g. info@yourbusiness.com.sb"
                className={inputClass}
              />
              {form.business_email.trim().length > 0 && !emailValid && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                  Enter a valid email address.
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="business-phone"
                className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
              >
                Business phone
              </label>
              <input
                id="business-phone"
                type="tel"
                value={form.business_phone}
                onChange={(e) => update('business_phone', e.target.value)}
                placeholder="e.g. +677 12345"
                className={inputClass}
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Tick only the taxes this business is registered for. If you&apos;re not sure, it&apos;s
              fine to leave them unticked — most small businesses aren&apos;t registered for most of
              these.
            </p>
            <CheckboxField
              label="Sales Tax"
              help="You charge 10% Sales Tax on prescribed services (telecoms, restaurant, professional, security, etc.)."
              checked={form.tax_registered_sales_tax}
              onChange={(v) => update('tax_registered_sales_tax', v)}
            />
            <CheckboxField
              label="Goods Tax"
              help="You import, manufacture, or wholesale goods. Most small shops and service businesses are not registered."
              checked={form.tax_registered_goods_tax}
              onChange={(v) => update('tax_registered_goods_tax', v)}
            />
            <CheckboxField
              label="Withholding Tax"
              help="You deduct tax from payments to contractors or suppliers and remit it to IRD."
              checked={form.tax_registered_withholding}
              onChange={(v) => update('tax_registered_withholding', v)}
            />
            <CheckboxField
              label="PAYE (employer)"
              help="You have employees and deduct PAYE from their wages."
              checked={form.tax_registered_paye_employer}
              onChange={(v) => update('tax_registered_paye_employer', v)}
            />
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Set up the things you sell. When you record income you&apos;ll pick one of these, and
              it sets the income account and tax for you. Start with these and refine later.
            </p>
            <div className="space-y-3">
              {form.categories.map((cat, i) => (
                <div key={cat.id} className="rounded-md border border-slate-200 p-3 dark:border-slate-700">
                  <div className="flex items-start gap-2">
                    <input
                      type="text"
                      value={cat.name}
                      onChange={(e) => updateCategory(i, { name: e.target.value })}
                      placeholder="e.g. Consulting, Food sales"
                      className={inputClass}
                    />
                    <button
                      type="button"
                      onClick={() => removeCategory(i)}
                      disabled={form.categories.length === 1}
                      className="inline-flex h-9 shrink-0 items-center rounded-md border border-slate-300 px-3 text-sm text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      Remove
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {availableTypes.length > 1 && (
                      <label className="text-xs text-slate-600 dark:text-slate-400">
                        <span className="mr-2">Type</span>
                        <select
                          value={cat.type}
                          onChange={(e) =>
                            updateCategory(i, { type: e.target.value as CategoryType })
                          }
                          className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        >
                          <option value="services">Service</option>
                          <option value="products">Product</option>
                        </select>
                      </label>
                    )}
                    <label className="text-xs text-slate-600 dark:text-slate-400">
                      <span className="mr-2">Tax</span>
                      <select
                        value={cat.tax ?? ''}
                        onChange={(e) => updateCategory(i, { tax: e.target.value || null })}
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                      >
                        <option value="">No tax</option>
                        {SALE_TAX_GROUPS.map((group) => {
                          const opts = applicableTaxOptions.filter((o) => o.group === group);
                          if (opts.length === 0) return null;
                          return (
                            <optgroup key={group} label={group}>
                              {opts.map((o) => (
                                <option key={o.code} value={o.code}>
                                  {o.label}
                                </option>
                              ))}
                            </optgroup>
                          );
                        })}
                      </select>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addCategory}
              className="inline-flex h-9 items-center rounded-md border border-dashed border-slate-300 px-3 text-sm font-medium text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              + Add category
            </button>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Goods Tax and other tax codes can be assigned to a category later in settings.
            </p>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-6">
            <CheckboxField
              label="This business is tax-exempt"
              help="For example, a registered church or an IRD-approved charity."
              checked={form.tax_exempt}
              onChange={(v) => update('tax_exempt', v)}
            />
            {form.tax_exempt && (
              <div>
                <label
                  htmlFor="exempt-reason"
                  className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300"
                >
                  Reason for exemption (optional)
                </label>
                <input
                  id="exempt-reason"
                  type="text"
                  value={form.tax_exempt_reason}
                  onChange={(e) => update('tax_exempt_reason', e.target.value)}
                  placeholder="e.g. Registered church"
                  className={inputClass}
                />
              </div>
            )}

            <div className="rounded-md bg-slate-50 p-4 text-sm dark:bg-slate-800/50">
              <p className="mb-2 font-medium text-slate-900 dark:text-slate-100">Review</p>
              <dl className="space-y-1 text-slate-600 dark:text-slate-400">
                <div className="flex justify-between gap-4">
                  <dt>Name</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.name.trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Structure</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.entity_type ? ENTITY_TYPE_LABELS[form.entity_type] : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Sells</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">{profileLabel}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Categories</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.categories.length > 0
                      ? form.categories
                          .map((c) => c.name.trim())
                          .filter((n) => n.length > 0)
                          .join(', ')
                      : '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Address</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.business_address.trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Email</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.business_email.trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Phone</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.business_phone.trim() || '—'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Registered for</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {registrations.length > 0 ? registrations.join(', ') : 'None'}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Tax-exempt</dt>
                  <dd className="text-right text-slate-900 dark:text-slate-100">
                    {form.tax_exempt ? 'Yes' : 'No'}
                  </dd>
                </div>
              </dl>
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                We&apos;ll set up your chart of accounts and tax codes automatically based on these
                answers.
              </p>
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0 || submitting}
            className="inline-flex h-10 items-center justify-center rounded-md border border-slate-300 bg-white px-5 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Back
          </button>

          {step < STEP_TITLES.length - 1 ? (
            <button
              type="button"
              onClick={goNext}
              disabled={!canAdvance}
              className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={submitting}
              className="inline-flex h-10 items-center justify-center rounded-md bg-brand-600 px-5 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Creating…' : 'Create business'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
